import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  RefreshCw,
  ExternalLink,
} from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@easyflow/ui-frontend/components/ui/tooltip';
import { useAuth } from '@/contexts/AuthContext';
import { useSocket } from '@/contexts/SocketContext';
import { useModules } from '@/contexts/ModuleContext';
import { usePermissions } from '@/contexts/PermissionContext';
import { cn } from '@/lib/utils';
import { apiClient } from '@/lib/api';
import { getMasterNavigation, getTenantNavigation, getFilteredNavigation, type NavItem, type NavChild } from '@/lib/navigation';
import type { FreeswitchStatusData, EslHealthData, CountData } from '@easyflow/shared';

export default function Sidebar() {
  const { user } = useAuth();
  const { isModuleEnabled } = useModules();
  const { hasPermission } = usePermissions();
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [freeSwitchConnected, setFreeSwitchConnected] = useState(false);
  const [freeSwitchLoading, setFreeSwitchLoading] = useState(false);
  const [eslConnected, setEslConnected] = useState<boolean | null>(null);
  const [eslLoading, setEslLoading] = useState(false);
  const [messengerUnread, setMessengerUnread] = useState(0);
  const autoCollapseTimer = useRef<NodeJS.Timeout | null>(null);
  const submenuAutoCollapseTimer = useRef<NodeJS.Timeout | null>(null);
  const SUBMENU_AUTO_COLLAPSE_MS = 8000; // 8 seconds

  const rawNavigation = user
    ? (user.is_master_tenant && user.role === 'super_admin')
      ? getMasterNavigation()
      : getTenantNavigation(user.tenantSlug)
    : [];
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const navigation = getFilteredNavigation(rawNavigation, isModuleEnabled, hasPermission, isAdmin);

  const checkFreeSwitchStatus = useCallback(async () => {
    if (!user) {
      setFreeSwitchConnected(false);
      return;
    }

    try {
      setFreeSwitchLoading(true);
      const res = await apiClient.get<FreeswitchStatusData>('/freeswitch/status');
      setFreeSwitchConnected(Boolean(res.data?.connected));
    } catch {
      setFreeSwitchConnected(false);
    } finally {
      setFreeSwitchLoading(false);
    }
  }, [user]);

  const checkEslStatus = useCallback(async () => {
    if (!user) {
      setEslConnected(null);
      return;
    }

    try {
      setEslLoading(true);
      const res = await apiClient.get<EslHealthData>('/system/esl-health');
      setEslConnected(res.data?.state === 'connected');
    } catch {
      setEslConnected(null);
    } finally {
      setEslLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user?.id) return;
    
    let isMounted = true;
    let backoffMs = 0;
    
    const checkStatuses = async () => {
      if (!isMounted) return;
      if (backoffMs > 0) {
        await new Promise(r => setTimeout(r, backoffMs));
        backoffMs = 0;
      }
      await checkFreeSwitchStatus();
      await checkEslStatus();
    };
    
    checkStatuses();
    const interval = setInterval(checkStatuses, 60000); // 60s instead of 30s
    
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [user?.id, checkEslStatus, checkFreeSwitchStatus]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth < 768) {
        setIsCollapsed(true);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const fetchUnread = useCallback(async () => {
    try {
      const res = await apiClient.get<CountData>('/messenger/unread-count');
      if (res.success) setMessengerUnread(res.data?.count || 0);
    } catch {}
  }, []);

  useEffect(() => {
    if (!user || !isModuleEnabled('messenger')) return;
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [user?.id, fetchUnread, isModuleEnabled, user]);

  const { socket, socketVersion } = useSocket();

  // Self-healing: su ogni (ri)connessione del socket (`socketVersion`++)
  // riallinea subito il badge chat. Il poll a 30s è la rete di sicurezza per
  // i buchi prolungati; questo dà immediatezza dopo un drop del pub/sub Redis.
  useEffect(() => {
    if (!user || !isModuleEnabled('messenger')) return;
    if (socketVersion === 0) return;
    fetchUnread();
  }, [socketVersion, user?.id, fetchUnread, isModuleEnabled, user]);

  useEffect(() => {
    if (!user || !isModuleEnabled('messenger')) return;
    const handleNewMessage = () => {
      fetchUnread();
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') fetchUnread();
    };
    window.addEventListener('messenger:new-message', handleNewMessage);
    document.addEventListener('visibilitychange', handleVisibility);

    if (socket) {
      const handleUnread = () => {
        setMessengerUnread(prev => prev + 1);
      };
      socket.on('chat:unread', handleUnread);
      return () => {
        window.removeEventListener('messenger:new-message', handleNewMessage);
        document.removeEventListener('visibilitychange', handleVisibility);
        socket.off('chat:unread', handleUnread);
      };
    }

    return () => {
      window.removeEventListener('messenger:new-message', handleNewMessage);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [user?.id, fetchUnread, socket, isModuleEnabled, user]);

  useEffect(() => {
    if (isMobile) return;

    const startAutoCollapse = () => {
      if (autoCollapseTimer.current) {
        clearTimeout(autoCollapseTimer.current);
      }
      autoCollapseTimer.current = setTimeout(() => {
        if (!isHovered) {
          setIsCollapsed(true);
          const event = new CustomEvent('sidebar-state-change', {
            detail: { collapsed: true }
          });
          window.dispatchEvent(event);
        }
      }, 3000);
    };

    if (!isCollapsed) {
      startAutoCollapse();
    }

    return () => {
      if (autoCollapseTimer.current) {
        clearTimeout(autoCollapseTimer.current);
      }
    };
  }, [isCollapsed, isHovered, isMobile]);

  React.useEffect(() => {
    // All submenus closed by default
    setExpandedItems([]);
  }, [user]);

  const toggleExpanded = (itemName: string) => {
    setExpandedItems(prev => {
      const isCurrentlyExpanded = prev.includes(itemName);
      
      if (isCurrentlyExpanded) {
        // Closing: just remove it
        return prev.filter(name => name !== itemName);
      } else {
        // Opening: add it and start auto-collapse timer
        if (submenuAutoCollapseTimer.current) {
          clearTimeout(submenuAutoCollapseTimer.current);
        }
        
        submenuAutoCollapseTimer.current = setTimeout(() => {
          setExpandedItems([]);
        }, SUBMENU_AUTO_COLLAPSE_MS);
        
        return [...prev, itemName];
      }
    });
  };
  
  // Reset submenu auto-collapse timer (called when clicking on submenu items)
  const resetSubmenuTimer = () => {
    if (submenuAutoCollapseTimer.current) {
      clearTimeout(submenuAutoCollapseTimer.current);
    }
    submenuAutoCollapseTimer.current = setTimeout(() => {
      setExpandedItems([]);
    }, SUBMENU_AUTO_COLLAPSE_MS);
  };
  
  // Cleanup submenu auto-collapse timer on unmount
  useEffect(() => {
    return () => {
      if (submenuAutoCollapseTimer.current) {
        clearTimeout(submenuAutoCollapseTimer.current);
      }
    };
  }, []);

  const handleMouseEnter = () => {
    if (!isMobile) {
      setIsHovered(true);
      if (autoCollapseTimer.current) {
        clearTimeout(autoCollapseTimer.current);
      }
      setTimeout(() => {
        if (isCollapsed) {
          setIsCollapsed(false);
          const event = new CustomEvent('sidebar-state-change', {
            detail: { collapsed: false }
          });
          window.dispatchEvent(event);
        }
      }, 200);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      setIsHovered(false);
    }
  };

  const toggleCollapse = () => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);

    const event = new CustomEvent('sidebar-state-change', {
      detail: { collapsed: newCollapsedState }
    });
    window.dispatchEvent(event);
  };

  const location = useLocation();
  const navigate = useNavigate();

  const isChildActive = (childHref: string) => {
    if (!childHref.includes('?')) return false;
    const [pathname, search] = childHref.split('?');
    if (location.pathname !== pathname) return false;
    const childParams = new URLSearchParams(search);
    const currentParams = new URLSearchParams(location.search);
    for (const [key, value] of childParams.entries()) {
      if (currentParams.get(key) !== value) return false;
    }
    return true;
  };

  const renderNavItem = (item: NavItem) => {
    if (item.children) {
      const visibleChildren = item.children;
      const isExpanded = expandedItems.includes(item.name);
      const parentHrefPath = item.href ? item.href.split('?')[0] : null;
      const matchesOwnHref = !!parentHrefPath && (
        item.exact
          ? location.pathname === parentHrefPath
          : (location.pathname === parentHrefPath || location.pathname.startsWith(parentHrefPath + '/'))
      );
      const matchesAnyChild = visibleChildren.some((child: NavChild) => {
        if (!child.href || child.newWindow) return false;
        const [childPath, childSearch] = child.href.split('?');
        if (childSearch) {
          if (location.pathname !== childPath) return false;
          const childParams = new URLSearchParams(childSearch);
          const currentParams = new URLSearchParams(location.search);
          for (const [k, v] of childParams.entries()) {
            if (currentParams.get(k) !== v) return false;
          }
          return true;
        }
        return location.pathname === childPath || location.pathname.startsWith(childPath + '/');
      });
      const isParentActive = matchesOwnHref || matchesAnyChild;
      return (
        <div key={item.name}>
          <button
            onClick={() => {
              if (item.href) navigate(item.href);
              toggleExpanded(item.name);
            }}
            className={cn(
              "group flex items-center w-full px-2 py-2 text-sm font-medium rounded-md",
              "hover:translate-x-1 hover:scale-[1.02]",
              "transition-all duration-200 ease-smooth",
              isCollapsed && "justify-center",
              isParentActive
                ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg shadow-blue-500/30'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50/50 dark:hover:from-blue-900/30 dark:hover:to-indigo-900/30 hover:text-blue-700 dark:hover:text-blue-400'
            )}
            title={isCollapsed ? item.name : undefined}
          >
            <item.icon className={cn("h-5 w-5 flex-shrink-0", isCollapsed ? "" : "mr-3")} />
            {!isCollapsed && (
              <>
                <span className="truncate">{item.name}</span>
                {item.moduleKey === 'messenger' && messengerUnread > 0 && (
                  <span className="ml-2 inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 text-[10px] font-bold rounded-full bg-red-500 text-white">
                    {messengerUnread > 99 ? '99+' : messengerUnread}
                  </span>
                )}
                {isExpanded ? (
                  <ChevronDown className="ml-auto h-4 w-4" />
                ) : (
                  <ChevronRight className="ml-auto h-4 w-4" />
                )}
              </>
            )}
            {isCollapsed && item.moduleKey === 'messenger' && messengerUnread > 0 && (
              <span className="absolute top-0 right-0 h-2.5 w-2.5 rounded-full bg-red-500" />
            )}
          </button>
          {isExpanded && !isCollapsed && (
            <div className="ml-4 space-y-1 animate-fadeIn">
              {visibleChildren.map((child: NavChild) => {
                if (child.newWindow) {
                  return (
                    <button
                      key={child.name}
                      onClick={() => { resetSubmenuTimer(); const w = 1024, h = 720, left = (screen.width - w) / 2, top = (screen.height - h) / 2; window.open(child.href, 'pos-window', `popup=yes,location=no,toolbar=no,menubar=no,status=no,width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`); }}
                      className={cn(
                        "group flex items-center w-full px-2 py-2 text-sm font-medium rounded-md",
                        "transition-all duration-200 ease-smooth",
                        "hover:translate-x-1 hover:scale-[1.02]",
                        'text-gray-600 dark:text-gray-400 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50/50 dark:hover:from-blue-900/20 dark:hover:to-indigo-900/20 hover:text-blue-700 dark:hover:text-blue-400'
                      )}
                    >
                      <child.icon className="mr-3 h-4 w-4 flex-shrink-0" />
                      {child.name}
                      <ExternalLink className="ml-auto h-3 w-3 opacity-40" />
                    </button>
                  );
                }
                const hasQueryParams = child.href.includes('?');
                if (hasQueryParams) {
                  const active = isChildActive(child.href);
                  return (
                    <button
                      key={child.name}
                      onClick={() => { resetSubmenuTimer(); navigate(child.href); }}
                      className={cn(
                        "group flex items-center w-full px-2 py-2 text-sm font-medium rounded-md",
                        "transition-all duration-200 ease-smooth",
                        "hover:translate-x-1 hover:scale-[1.02]",
                        active
                          ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg shadow-blue-500/30'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50/50 dark:hover:from-blue-900/20 dark:hover:to-indigo-900/20 hover:text-blue-700 dark:hover:text-blue-400'
                      )}
                    >
                      <child.icon className="mr-3 h-4 w-4 flex-shrink-0" />
                      {child.name}
                    </button>
                  );
                }
                return (
                <NavLink
                  key={child.name}
                  to={child.href}
                  onClick={resetSubmenuTimer}
                  className={({ isActive }) =>
                    cn(
                      "group flex items-center px-2 py-2 text-sm font-medium rounded-md",
                      "transition-all duration-200 ease-smooth",
                      "hover:translate-x-1 hover:scale-[1.02]",
                      isActive
                        ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg shadow-blue-500/30'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50/50 dark:hover:from-blue-900/20 dark:hover:to-indigo-900/20 hover:text-blue-700 dark:hover:text-blue-400'
                    )
                  }
                >
                  <child.icon className="mr-3 h-4 w-4 flex-shrink-0" />
                  {child.name}
                </NavLink>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    // Task #3585 — href assoluti (console master su origin dedicato):
    // navigazione full-page cross-origin, NavLink non è applicabile.
    if (item.href && /^https?:\/\//.test(item.href)) {
      return (
        <a
          key={item.name}
          href={item.href}
          className={cn(
            "group flex items-center px-2 py-2 text-sm font-medium rounded-md relative",
            "transition-all duration-200 ease-smooth",
            "hover:translate-x-1 hover:scale-[1.02]",
            isCollapsed && "justify-center",
            'text-gray-700 dark:text-gray-300 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50/50 dark:hover:from-blue-900/30 dark:hover:to-indigo-900/30 hover:text-blue-700 dark:hover:text-blue-400'
          )}
          title={isCollapsed ? item.name : undefined}
        >
          <item.icon className={cn("h-5 w-5 flex-shrink-0", isCollapsed ? "" : "mr-3")} />
          {!isCollapsed && <span className="truncate">{item.name}</span>}
        </a>
      );
    }

    return (
      <NavLink
        key={item.name}
        to={item.href!}
        end={item.exact}
        className={({ isActive }) =>
          cn(
            "group flex items-center px-2 py-2 text-sm font-medium rounded-md relative",
            "transition-all duration-200 ease-smooth",
            "hover:translate-x-1 hover:scale-[1.02]",
            isCollapsed && "justify-center",
            isActive
              ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg shadow-blue-500/30'
              : 'text-gray-700 dark:text-gray-300 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50/50 dark:hover:from-blue-900/30 dark:hover:to-indigo-900/30 hover:text-blue-700 dark:hover:text-blue-400'
          )
        }
        title={isCollapsed ? item.name : undefined}
      >
        <item.icon className={cn("h-5 w-5 flex-shrink-0", isCollapsed ? "" : "mr-3")} />
        {!isCollapsed && <span className="truncate">{item.name}</span>}
      </NavLink>
    );
  };

  return (
    <div
      className={cn(
        "hidden md:flex md:flex-col fixed left-0 top-0 h-full z-40",
        "glass-nav transition-all duration-300 ease-smooth",
        isCollapsed ? "w-16" : "w-64"
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="flex flex-col flex-grow pt-2 overflow-y-auto">
        {/* Header with logo and toggle */}
        <div className={cn(
          "flex-shrink-0 relative",
          isCollapsed ? "px-0" : "px-4"
        )}>
          {isCollapsed ? (
            <div className="flex flex-col items-center">
              <img 
                src="/logos/easyflow-icon-v5.svg" 
                alt="EasyFlow" 
                className="h-10 w-10 mb-2" 
              />
              {!isMobile && (
                <button
                  onClick={toggleCollapse}
                  className="p-1.5 rounded-full bg-white/80 dark:bg-gray-800/80 shadow-sm border border-gray-200/50 dark:border-gray-700/50 hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:scale-110 transition-all duration-200"
                  title="Espandi menu"
                >
                  <ChevronRight className="h-3.5 w-3.5 text-gray-600 dark:text-gray-400" />
                </button>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <img 
                  src="/logos/easyflow-full-v3.svg" 
                  alt="EasyFlow" 
                  className="h-9" 
                />
              </div>
              {!isMobile && (
                <button
                  onClick={toggleCollapse}
                  className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 hover:scale-110 transition-all duration-200"
                  title="Comprimi menu"
                >
                  <ChevronLeft className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="mt-3 flex-grow flex flex-col">
          <nav className="flex-1 px-2 pb-2 space-y-0.5">
            {navigation.map(renderNavItem)}
          </nav>
        </div>

        {/* Footer with status indicators */}
        <div className="flex-shrink-0 border-t border-white/20 p-3">
          {/* Status indicators */}
          {!isCollapsed ? (
            <div className="space-y-1.5 mb-2">
              {/* System Online */}
              <div className="flex items-center justify-between px-2 py-1.5 rounded-md bg-white/30 dark:bg-gray-800/30">
                <div className="flex items-center space-x-2">
                  <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-xs text-gray-600 dark:text-gray-400">System</span>
                </div>
                <span className="text-xs font-medium text-green-600 dark:text-green-400">Online</span>
              </div>

              {/* FreeSWITCH Status */}
              <div className="flex items-center justify-between px-2 py-1.5 rounded-md bg-white/30 dark:bg-gray-800/30">
                <div className="flex items-center space-x-2">
                  <div className={cn(
                    "h-2 w-2 rounded-full",
                    freeSwitchConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'
                  )}></div>
                  <span className="text-xs text-gray-600 dark:text-gray-400">FreeSWITCH</span>
                </div>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={checkFreeSwitchStatus}
                    disabled={freeSwitchLoading}
                    className="p-0.5 hover:bg-white/50 rounded transition-all duration-200"
                  >
                    <RefreshCw className={cn(
                      "h-3 w-3 text-gray-400",
                      freeSwitchLoading && 'animate-spin'
                    )} />
                  </button>
                  <span className={cn(
                    "text-xs font-medium",
                    freeSwitchConnected ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                  )}>
                    {freeSwitchConnected ? 'Online' : 'Offline'}
                  </span>
                </div>
              </div>

              {/* ESL Connection Status */}
              <div className="flex items-center justify-between px-2 py-1.5 rounded-md bg-white/30 dark:bg-gray-800/30">
                <div className="flex items-center space-x-2">
                  <div className={cn(
                    "h-2 w-2 rounded-full",
                    eslConnected === true ? 'bg-green-500 animate-pulse' : eslConnected === false ? 'bg-red-500' : 'bg-yellow-500'
                  )}></div>
                  <span className="text-xs text-gray-600 dark:text-gray-400">ESL</span>
                </div>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={checkEslStatus}
                    disabled={eslLoading}
                    className="p-0.5 hover:bg-white/50 rounded transition-all duration-200"
                  >
                    <RefreshCw className={cn(
                      "h-3 w-3 text-gray-400",
                      eslLoading && 'animate-spin'
                    )} />
                  </button>
                  <span className={cn(
                    "text-xs font-medium",
                    eslConnected === true ? 'text-green-600 dark:text-green-400' : eslConnected === false ? 'text-red-600 dark:text-red-400' : 'text-yellow-600 dark:text-yellow-400'
                  )}>
                    {eslConnected === true ? 'Online' : eslConnected === false ? 'Offline' : 'N/A'}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center space-y-1.5 mb-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse cursor-help"></div>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    <p>System Online</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className={cn(
                      "h-2 w-2 rounded-full cursor-help",
                      freeSwitchConnected ? 'bg-green-500 animate-pulse' : 'bg-red-500'
                    )}></div>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    <p>FreeSWITCH {freeSwitchConnected ? 'Online' : 'Offline'}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className={cn(
                      "h-2 w-2 rounded-full cursor-help",
                      eslConnected === true ? 'bg-green-500 animate-pulse' : eslConnected === false ? 'bg-red-500' : 'bg-yellow-500'
                    )}></div>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    <p>ESL {eslConnected === true ? 'Online' : eslConnected === false ? 'Offline' : 'N/A'}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}

          {/* Crafted by Easydigitalgroup */}
          {!isCollapsed ? (
            <div className="text-center pt-2 border-t border-white/10">
              <span className="text-xs text-gray-400 dark:text-gray-500">
                Crafted by{' '}
                <a
                  href="https://easydigitalgroup.it"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 hover:underline transition-colors inline-flex items-center gap-0.5"
                >
                  Easydigitalgroup
                  <ExternalLink className="h-2.5 w-2.5" />
                </a>
              </span>
            </div>
          ) : (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <a
                    href="https://easydigitalgroup.it"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex justify-center pt-2 border-t border-white/10"
                  >
                    <ExternalLink className="h-3 w-3 text-gray-400 hover:text-blue-500 transition-colors" />
                  </a>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p>Crafted by Easydigitalgroup</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>
    </div>
  );
}
